"use client";

import { useEffect, useState } from "react";

import AIChat from "@/components/AIChat";
import { SideNavSlot } from "@/components/nav/SideNavSlots";
import { hasOpenSabineFlag } from "@/lib/sabine-chat/open-link";

/**
 * Sabine's chat about this page, registered with the side nav: the nav shows
 * the Ask button above the page and opens the chat in the coach pane beside
 * the rail. `placeholder` is what the page lets you ask; the button and the
 * composer both say it. Pass `open` and `onOpenChange` when the page opens
 * the chat itself too (a one-click coaching action); leave them out and the
 * nav owns it, closed until the button is pressed, or open from the start with
 * `defaultOpen` (the home page). Everything else is AIChat's, unless `chat`
 * hands over an element that renders AIChat itself (the playbook's, with its
 * own handlers).
 *
 * A chat link (`openSabineHref`) asks for the pane on arrival: the flag is
 * read when this page's chat mounts, so it is never taken for the chat of the
 * page being left.
 *
 * @param {object} props
 * @param {boolean} [props.enabled]  False registers nothing (no transcript to coach on)
 * @param {string} props.placeholder
 * @param {boolean} [props.defaultOpen]
 * @param {boolean} [props.open]
 * @param {(open: boolean) => void} [props.onOpenChange]
 * @param {import("react").ReactElement} [props.chat]
 */
export default function NavCoach({
  enabled = true,
  placeholder,
  defaultOpen = false,
  open,
  onOpenChange,
  chat,
  ...chatProps
}) {
  const [openOnArrival, setOpenOnArrival] = useState(false);
  useEffect(() => setOpenOnArrival(hasOpenSabineFlag()), []);

  const coach = enabled
    ? {
        placeholder,
        defaultOpen,
        openOnArrival,
        open,
        onOpenChange,
        chat: chat || <AIChat {...chatProps} inputPlaceholder={placeholder} />,
      }
    : null;
  return <SideNavSlot coach={coach} />;
}
